import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FilePlus2, Send } from "lucide-react";
import { toast } from "sonner";

import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Loading } from "../../components/shared";

function DynamicField({ field, value, onChange }) {
  if (field.type === "LONG_TEXT") {
    return (
      <Textarea
        data-testid={`request-field-${field.key}`}
        onChange={(event) => onChange(event.target.value)}
        value={value || ""}
      />
    );
  }
  if (["DROPDOWN", "RADIO", "YES_NO"].includes(field.type)) {
    const options = field.type === "YES_NO" ? ["Yes", "No"] : field.options;
    return (
      <select
        className="w-full rounded-md border border-slate-300 bg-white px-3 py-2"
        data-testid={`request-field-${field.key}`}
        onChange={(event) => onChange(event.target.value)}
        value={value || ""}
      >
        <option value="">Select</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    );
  }
  if (field.type === "CHECKBOX") {
    return (
      <input
        checked={Boolean(value)}
        data-testid={`request-field-${field.key}`}
        onChange={(event) => onChange(event.target.checked)}
        type="checkbox"
      />
    );
  }
  return (
    <Input
      data-testid={`request-field-${field.key}`}
      onChange={(event) => onChange(event.target.value)}
      type={
        field.type === "DATE"
          ? "date"
          : field.type === "NUMBER"
            ? "number"
            : "text"
      }
      value={value || ""}
    />
  );
}

export default function ResidentFormalRequests() {
  const [types, setTypes] = useState(null);
  const [requests, setRequests] = useState([]);
  const [units, setUnits] = useState([]);
  const [typeId, setTypeId] = useState("");
  const [unitId, setUnitId] = useState("");
  const [values, setValues] = useState({});
  const [draft, setDraft] = useState(null);
  const [file, setFile] = useState(null);
  const [ruleKey, setRuleKey] = useState("");

  const load = async () => {
    try {
      const [typeResponse, requestResponse, contextResponse] =
        await Promise.all([
          api.get("/resident-requests/request-types"),
          api.get("/resident-requests/requests"),
          api.get("/resident-requests/context"),
        ]);
      setTypes(typeResponse.data);
      setRequests(requestResponse.data);
      setUnits(contextResponse.data.units);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not load formal requests",
      );
      setTypes([]);
    }
  };

  useEffect(() => {
    load();
  }, []);
  const selectedType = types?.find((item) => item.request_type_id === typeId);

  const createDraft = async () => {
    try {
      const response = await api.post("/resident-requests/requests", {
        request_type_id: typeId,
        unit_id: unitId,
        values,
      });
      setDraft(response.data);
      toast.success("Request draft created");
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not create request draft",
      );
    }
  };

  const upload = async () => {
    if (!file || !draft) return;
    const data = new FormData();
    data.append("file", file);
    data.append("rule_key", ruleKey);
    try {
      await api.post(
        `/resident-requests/requests/${draft.request_id}/attachments`,
        data,
      );
      toast.success("Attachment added");
      setFile(null);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Could not upload attachment",
      );
    }
  };

  const submit = async () => {
    try {
      await api.post(`/resident-requests/requests/${draft.request_id}/submit`);
      toast.success("Formal request submitted");
      setDraft(null);
      setValues({});
      await load();
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Complete required information first",
      );
    }
  };

  if (!types) return <Loading />;
  return (
    <div data-testid="resident-formal-requests-page">
      <header className="border-b border-slate-200 pb-6">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
          Society applications
        </p>
        <h1 className="mt-2 font-display text-4xl font-bold text-slate-900">
          Requests & Certificates
        </h1>
      </header>
      <section
        className="mt-6 border border-slate-200 bg-white p-5"
        data-testid="resident-new-request"
      >
        <h2 className="font-display text-xl font-semibold">
          New formal request
        </h2>
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <select
            className="rounded-md border border-slate-300 bg-white px-3 py-2"
            data-testid="request-type-select"
            onChange={(event) => {
              setTypeId(event.target.value);
              setValues({});
            }}
            value={typeId}
          >
            <option value="">Select request type</option>
            {types.map((item) => (
              <option key={item.request_type_id} value={item.request_type_id}>
                {item.name}
              </option>
            ))}
          </select>
          <select
            className="rounded-md border border-slate-300 bg-white px-3 py-2"
            data-testid="request-unit-select"
            onChange={(event) => setUnitId(event.target.value)}
            value={unitId}
          >
            <option value="">Select authorized unit</option>
            {units.map((unit) => (
              <option key={unit.unit_id} value={unit.unit_id}>
                {unit.unit_number}
              </option>
            ))}
          </select>
        </div>
        {selectedType && (
          <>
            <p className="mt-4 text-sm text-slate-600">
              {selectedType.instructions}
            </p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {selectedType.fields.map((field) => (
                <label className="text-sm" key={field.key}>
                  <span className="mb-1 block font-medium">
                    {field.label}
                    {field.required ? " *" : ""}
                  </span>
                  <DynamicField
                    field={field}
                    onChange={(value) =>
                      setValues({ ...values, [field.key]: value })
                    }
                    value={values[field.key]}
                  />
                </label>
              ))}
            </div>
            <Button
              className="mt-5"
              data-testid="request-create-draft"
              onClick={createDraft}
            >
              <FilePlus2 className="h-4 w-4" />
              Create draft
            </Button>
          </>
        )}
        {draft && (
          <div
            className="mt-5 border-t border-slate-200 pt-5"
            data-testid="request-draft-actions"
          >
            <p className="text-sm font-semibold">
              Draft {draft.request_number}
            </p>
            <div className="mt-3 flex flex-wrap gap-3">
              <input
                data-testid="request-attachment-input"
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                type="file"
              />
              <select
                className="rounded-md border border-slate-300 px-2 text-sm"
                data-testid="request-attachment-rule"
                onChange={(event) => setRuleKey(event.target.value)}
                value={ruleKey}
              >
                <option value="">Supporting document</option>
                {selectedType.document_rules.map((rule) => (
                  <option key={rule.key} value={rule.key}>
                    {rule.label}
                  </option>
                ))}
              </select>
              <Button
                data-testid="request-upload-attachment"
                onClick={upload}
                size="sm"
                variant="outline"
              >
                Upload
              </Button>
              <Button data-testid="request-submit" onClick={submit} size="sm">
                <Send className="h-4 w-4" />
                Submit
              </Button>
            </div>
          </div>
        )}
      </section>
      <section className="mt-8" data-testid="resident-request-list">
        <h2 className="font-display text-2xl font-semibold">My requests</h2>
        <div className="mt-3 space-y-3">
          {requests.length ? (
            requests.map((item) => (
              <Link
                className={
                  "block border border-slate-200 bg-white p-4 transition-colors " +
                  "hover:border-blue-500 hover:bg-blue-50"
                }
                data-testid={`resident-request-${item.request_id}`}
                key={item.request_id}
                to={`/resident/requests/${item.request_id}`}
              >
                <div className="flex flex-wrap justify-between gap-2">
                  <strong>{item.request_type}</strong>
                  <span className="text-xs font-semibold text-blue-700">
                    {item.status}
                  </span>
                </div>
                <p className="mt-2 text-sm text-slate-600">
                  {item.request_number} · Unit {item.unit || "—"}
                </p>
                {item.action_required && (
                  <p className="mt-2 text-xs font-semibold text-amber-700">
                    Action required
                  </p>
                )}
              </Link>
            ))
          ) : (
            <p
              className="border border-dashed border-slate-300 bg-white p-5 text-sm text-slate-600"
              data-testid="resident-request-empty"
            >
              No formal requests yet.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}
